import { useCallback, useEffect, useState, type ReactElement } from 'react';

import { Composer } from './components/composer.tsx';
import { Conversation } from './components/conversation.tsx';
import { Header } from './components/header.tsx';
import { Inspector } from './components/inspector.tsx';
import { WireTape } from './components/wire-tape.tsx';
import { type ChatController, useChat } from './hooks/use-chat.ts';
import { type EndlessController, useEndless } from './hooks/use-endless.ts';
import { useScenarios } from './hooks/use-scenarios.ts';
import { type SettingsController, useSettings } from './hooks/use-settings.ts';
import { SettingsRoute } from './routes/settings.tsx';
import { Styleguide } from './routes/styleguide.tsx';
import { createInitialState } from '@harness/stream-core';
import './styles/app.css';
import './styles/instrument.css';

type Route = '/' | '/settings' | '/styleguide';

function routeFromPath(pathname: string): Route {
  if (pathname.startsWith('/settings')) return '/settings';
  if (pathname.startsWith('/styleguide')) return '/styleguide';
  return '/';
}

function Chat({
  chat,
  route,
  onNavigate,
  endless,
}: {
  chat: ChatController;
  route: Route;
  onNavigate: (path: string) => void;
  endless: EndlessController;
}): ReactElement {
  // Open by default on a laptop or wider, where the inspector docks as a
  // column beside the conversation. Below that it is a bottom drawer that
  // overlays the composer, so a phone or tablet starts with it closed
  // rather than covering the one control a reader needs first.
  const [inspectorOpen, setInspectorOpen] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  const instrumentState = chat.liveState ?? createInitialState();

  // A headless loop hammering the server and a hand driven send are two
  // different operators reaching for the same machine at once. Starting a
  // send by hand is a clear signal the reader wants to drive, so it stops
  // the loop rather than racing it.
  const send = useCallback(
    (message: string) => {
      if (endless.running) endless.stop();
      chat.send(message);
    },
    [chat, endless],
  );
  const chatForHeader: ChatController = { ...chat, send };

  return (
    <div className="app">
      <Header
        chat={chatForHeader}
        inspectorOpen={inspectorOpen}
        onToggleInspector={() => {
          setInspectorOpen((current) => !current);
        }}
        route={route}
        onNavigate={onNavigate}
      />
      <div className={`app__body${inspectorOpen ? ' app__body--with-inspector' : ''}`}>
        <main className="app__conversation">
          <Conversation
            turns={chat.turns}
            liveTurnId={chat.liveTurnId}
            actions={{
              onRetry: chat.retry,
              onCancel: chat.cancel,
              onRestart: () => {
                send(`Run the ${chat.scenario} scenario again.`);
              },
            }}
          />
          <Composer disabled={chat.busy} onSend={send} />
        </main>
        {inspectorOpen && (
          <aside className="app__inspector-column" aria-label="Inspector">
            <Inspector state={instrumentState} />
          </aside>
        )}
      </div>
      {/* The tape is its own landmark; see the note on the section it renders. */}
      <WireTape state={instrumentState} />
    </div>
  );
}

/**
 * Three routes, no router dependency: history.pushState and a pathname
 * switch, matching the two route version this replaces. Settings and
 * scenarios are fetched once here and threaded down, since both the chat
 * send path and the settings tuning controls need the same knob manifest
 * and the same persisted values.
 */
export function App(): ReactElement {
  const [route, setRoute] = useState<Route>(() => routeFromPath(window.location.pathname));
  const settingsCtl: SettingsController = useSettings();
  const scenarios = useScenarios();
  const endless = useEndless(scenarios, settingsCtl.settings);
  const chat = useChat(settingsCtl.settings, scenarios);

  const navigate = useCallback((path: string) => {
    window.history.pushState(null, '', path);
    setRoute(routeFromPath(path));
  }, []);

  useEffect(() => {
    const onPopState = (): void => {
      setRoute(routeFromPath(window.location.pathname));
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  // Endless mode's own tally lives on the Settings tab. Leaving it stops the
  // loop rather than leaving it running unattended: a pass and fail count
  // nobody is watching is not evidence of anything.
  const { running: endlessRunning, stop: stopEndless } = endless;
  useEffect(() => {
    if (route !== '/settings' && endlessRunning) stopEndless();
  }, [route, endlessRunning, stopEndless]);

  if (route === '/styleguide') {
    return <Styleguide route={route} onNavigate={navigate} />;
  }

  if (route === '/settings') {
    return (
      <SettingsRoute
        route={route}
        onNavigate={navigate}
        settingsCtl={settingsCtl}
        scenarios={scenarios}
        endless={endless}
      />
    );
  }

  return <Chat chat={chat} route={route} onNavigate={navigate} endless={endless} />;
}
